import {
  clientsOpportunitiesListQueryKey,
  leadsLogsListQueryKey,
  leadsOpportunitiesListQueryKey,
  opportunitiesListQueryKey,
  opportunitiesLogsListQueryKey,
  opportunitiesPartialUpdateMutation,
  opportunitiesRetrieveQueryKey,
  opportunitiesStatusesListOptions,
  PatchedOpportunityWritable,
  ReturnOpportunityReadable,
  ReturnOpportunityStatusReadable
} from '@crm/api'
import { defaultNonPagination } from '@crm/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Badge, Button, Dropdown, Flex, MenuProps, Tooltip } from 'antd'
import { ArrowDown2 } from 'iconsax-react'

import { ReactNode, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddContractModal } from './AddContractModal'
import { OpportunityRefusalReasonModal } from './OpportunityRefusalReasonModal'
import { useStyles } from './styles'

type Props = {
  opportunity: ReturnOpportunityReadable
  bordered?: boolean
  disabled?: boolean
}

const StatusLabel = ({ name, color }: { name?: ReactNode; color?: string }) => {
  const { t } = useLocales()

  return (
    <Flex align="center" gap={8} className="w-full">
      <Badge color={color} />

      <Text className="w-full" isEllipsis>
        {name || t('opportunity.none')}
      </Text>
    </Flex>
  )
}

export const OpportunityStatuses = ({
  bordered,
  opportunity,
  disabled
}: Props) => {
  const { t } = useLocales()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { styles } = useStyles()
  const { notification } = App.useApp()

  const { id: leadId } = useParams()

  const [refusalStatusId, setRefusalStatusId] = useState<number | null>(null)
  const [contractStatusId, setContractStatusId] = useState<number | null>(null)

  const { data } = useQuery(
    opportunitiesStatusesListOptions({
      query: {
        ...defaultNonPagination,
        ...(opportunity?.status?.pipeline?.id && {
          pipeline: opportunity?.status?.pipeline?.id
        })
      }
    })
  )

  type StatusWithContract = ReturnOpportunityStatusReadable & {
    requires_contract?: boolean
  }

  const statusMap = new Map<number, StatusWithContract>(
    data?.results.map((s) => [s.id, s]) ?? []
  )

  const allowedIds = opportunity.status?.allowed_transition_statuses

  const items: MenuProps['items'] = data?.results.map(({ name, color, id }) => {
    const isDisabled =
      allowedIds !== undefined &&
      allowedIds !== null &&
      !allowedIds.includes(id) &&
      id !== opportunity.status?.id

    return {
      key: id,
      disabled: isDisabled,
      label: (
        <Tooltip
          title={isDisabled ? t('transition_not_allowed') : undefined}
          placement="left"
        >
          <div style={{ pointerEvents: 'all' }}>
            <StatusLabel name={name} color={color} />
          </div>
        </Tooltip>
      )
    }
  })

  const { mutate, isPending } = useMutation({
    ...opportunitiesPartialUpdateMutation(),
    onError: (error) => showApiErrors(error),
    onSuccess: () => {
      invalidateQueries([
        leadsOpportunitiesListQueryKey({ path: { lead_id: String(leadId) } }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesListQueryKey(),
        clientsOpportunitiesListQueryKey({
          path: { client_id: String(leadId) }
        }),
        opportunitiesLogsListQueryKey({
          path: { opportunity_id: String(opportunity.id) }
        }),
        opportunitiesRetrieveQueryKey({ path: { id: opportunity.id } })
      ])
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const changeStatus = (statusId: number) => {
    if (!opportunity.id) return

    const body = {
      lead: opportunity.lead?.id,
      status: statusId
    } as unknown as PatchedOpportunityWritable

    mutate({ path: { id: opportunity.id }, body })
  }

  const onClick = ({ key }: { key: string }) => {
    const statusId = Number(key)
    const status = statusMap.get(statusId)

    if (status?.requires_refusal_reason) {
      setRefusalStatusId(statusId)
    } else if (status?.requires_contract) {
      setContractStatusId(statusId)
    } else {
      changeStatus(statusId)
    }
  }

  return (
    <>
      <Dropdown
        disabled={disabled}
        placement="bottomLeft"
        trigger={['click']}
        overlayClassName={styles.opportunityStatuses}
        menu={{
          items,
          selectedKeys: [String(opportunity.status?.id)],
          onClick
        }}
      >
        <Button
          block
          loading={isPending}
          icon={<ArrowDown2 size={16} variant="Bold" />}
          iconPosition="end"
          type={bordered ? 'default' : 'text'}
        >
          <StatusLabel
            name={opportunity?.status?.name}
            color={opportunity?.status?.color}
          />
        </Button>
      </Dropdown>

      <OpportunityRefusalReasonModal
        open={!!refusalStatusId}
        opportunityId={opportunity.id}
        onConfirm={() => {
          if (refusalStatusId) changeStatus(refusalStatusId)
          setRefusalStatusId(null)
        }}
        onCancel={() => setRefusalStatusId(null)}
      />

      <AddContractModal
        open={!!contractStatusId}
        opportunityId={opportunity.id}
        onSuccess={() => {
          if (contractStatusId) changeStatus(contractStatusId)
          setContractStatusId(null)
        }}
        onCancel={() => setContractStatusId(null)}
      />
    </>
  )
}
